import {
    CloudOfferingKey,
    CloudProductKey,
    CurrencyISOCode,
    FormattedPriceCalculationResponse as BaseFormattedPriceCalculationResponse,
    OfferingHamsMap,
    OfferingSKUMap,
    PricingPeriod,
    PricingPlans
} from "@/components/wpl/pricing/src/price-finder/constants.tsx";
import {
    extractPricingTiersFromEntry,
    GetCcpTiers
} from "@/components/wpl/pricing/src/price-finder/utils/extract-pricing-tiers-from-entry.ts";
import { calculatePrice } from "@/components/wpl/pricing/src/price-finder/utils/price-calculation.ts";
import { getFreePriceCalc } from "@/components/wpl/pricing/src/price-finder/utils/use-price-finder-data.ts";
import {
    CCPOfferingContent,
    CCPPricingPlanItem
} from "@/contentful/product-catalog";

import { bitbucketOfferingSkus } from "../configs/bitbucket.pricing-calculator-config.ts";
import { rovoDevOfferingSkus } from "../configs/rovo-dev.pricing-calculator-config.ts";

export interface CalculatePlanPricingArgs {
    ccpPricingData: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
    ccpOfferingSKU: string | undefined;
    numUnits: number;
    period: PricingPeriod;
    currencyISOCode: CurrencyISOCode;
    localeISOCode: string;
}

export type FormattedPriceCalculationResponse =
    BaseFormattedPriceCalculationResponse & {
        offeringSku: string;
    };

const hamsPricingDataStub = {} as OfferingHamsMap<
    CloudProductKey,
    PricingPlans
>;
const hamsProductKeyStub = "" as CloudProductKey;

export function calculatePlanPricing(
    args: CalculatePlanPricingArgs
): FormattedPriceCalculationResponse {
    const {
        ccpPricingData,
        ccpOfferingSKU,
        period,
        numUnits,
        currencyISOCode,
        localeISOCode
    } = args;

    if (numUnits === 0) {
        return {
            offeringSku: ccpOfferingSKU as string,
            ...getFreePriceCalc(currencyISOCode)
        };
    } else {
        let getCcpTiers: GetCcpTiers | undefined = undefined;
        if (
            ccpOfferingSKU === rovoDevOfferingSkus.standard ||
            ccpOfferingSKU === bitbucketOfferingSkus.standard ||
            ccpOfferingSKU === bitbucketOfferingSkus.premium
        ) {
            // rovodev-standard price-plans contain both user and rovodev-credits charge-items
            // bitbucket-standard and bitbucket-premium price-plans contain both user and bitbucket-credits charge-items as well
            // so, we need to get the plan with the user charge-item
            // todo: GWP-8253 move out of reusable calc logic
            getCcpTiers = (pricingPlans) =>
                pricingPlans.items.find(
                    (item) => item.chargeElement === "user"
                ) as CCPPricingPlanItem;
        }

        const pricingTiers = extractPricingTiersFromEntry(
            ccpPricingData,
            hamsPricingDataStub,
            period,
            currencyISOCode,
            ccpOfferingSKU,
            hamsProductKeyStub,
            getCcpTiers
        );

        if (!pricingTiers) {
            console.log(`Pricing tiers not found for ${period}.`);
            return {
                offeringSku: ccpOfferingSKU as string,
                ...getFreePriceCalc(currencyISOCode)
            };
        } else {
            return {
                offeringSku: ccpOfferingSKU as string,
                ...calculatePrice(
                    numUnits,
                    pricingTiers,
                    currencyISOCode,
                    period,
                    localeISOCode
                )
            };
        }
    }
}
